import * as React from "react";
import useApi from "@/hooks/useApi";
import {
  SectionCard,
  PrimaryButton,
  SecondaryButton,
  GhostButton,
  IconButton
} from "@/components/ds";
import { Building } from "@/components/ds/icons";
import { Severity } from "@velaro/velaro-component-lib";
import useToastAlert from "@/hooks/useToastAlert";

interface QnAPair {
  id: string;
  question: string;
  answer: string;
}

interface BotStatus {
  vectorEnabled: boolean;
  enableKbBotIndexing: boolean;
  hasAI: boolean;
}

interface IndexSource {
  sourceType: string;
  documentCount: number;
  totalChunks: number;
  lastIndexedAt: string | null;
  hasErrors: boolean;
}

interface Props {
  storeHash?: string;
  storeUrl?: string;
}

export default function BigCommerceBotKnowledge({
  storeHash,
  storeUrl
}: Props) {
  const api = useApi();
  const toastAlert = useToastAlert();
  const [status, setStatus] = React.useState<BotStatus | null>(null);
  const [pairs, setPairs] = React.useState<QnAPair[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [scanLoading, setScanLoading] = React.useState(false);
  const [scanDone, setScanDone] = React.useState(false);
  const [savedCount, setSavedCount] = React.useState<number | null>(null);
  const [lastSaved, setLastSaved] = React.useState<string | null>(null);
  const [fetchingPolicies, setFetchingPolicies] = React.useState(false);
  const [policyFetchResult, setPolicyFetchResult] = React.useState<
    null | { count: number } | { error: string }
  >(null);
  const [productIndex, setProductIndex] = React.useState<IndexSource | null>(
    null
  );
  const [syncing, setSyncing] = React.useState(false);

  React.useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [statusResp, qnaResp, sourcesResp] = await Promise.all([
          api.admin.get("BigCommerceBot/Status"),
          api.admin.get("BigCommerceBot/SuggestedQnA"),
          api.messaging.get("AzureIndexes/IntegrationSources").catch(() => null)
        ]);
        const statusData = await statusResp.json();
        const qnaData = await qnaResp.json();
        setStatus(
          statusData ?? {
            vectorEnabled: false,
            enableKbBotIndexing: false,
            hasAI: false
          }
        );
        // Handle both old array format and new { templates, savedCount, lastSaved } format
        const templates = Array.isArray(qnaData)
          ? qnaData
          : Array.isArray(qnaData?.templates)
            ? qnaData.templates
            : [];
        if (templates.length > 0) {
          setPairs(
            templates.map((t: any) => ({
              id: t.id ?? `pair_${Math.random()}`,
              question: t.question ?? "",
              answer: t.answer ?? ""
            }))
          );
        }
        if (!Array.isArray(qnaData) && qnaData?.savedCount != null) {
          setSavedCount(qnaData.savedCount);
          setLastSaved(qnaData.lastSaved ?? null);
        }
        if (sourcesResp?.ok) {
          const sourcesData = await sourcesResp.json();
          const bc = sourcesData?.sources?.find(
            (s: IndexSource) => s.sourceType === "bigcommerce_product"
          );
          setProductIndex(bc ?? null);
        }
      } catch {
        setStatus({
          vectorEnabled: false,
          enableKbBotIndexing: false,
          hasAI: false
        });
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function syncProducts() {
    setSyncing(true);
    try {
      const resp = await api.messaging.post("AzureIndexes/SyncIntegration", {
        source: "bigcommerce"
      });
      if (resp.ok) {
        toastAlert.displayToast(
          Severity.Success,
          "Product sync started. Your product count will update here in a few minutes."
        );
        setTimeout(async () => {
          try {
            const sourcesResp = await api.messaging.get(
              "AzureIndexes/IntegrationSources"
            );
            if (sourcesResp?.ok) {
              const sourcesData = await sourcesResp.json();
              const bc = sourcesData?.sources?.find(
                (s: IndexSource) => s.sourceType === "bigcommerce_product"
              );
              setProductIndex(bc ?? null);
            }
          } catch {
            /* silent */
          }
        }, 12000);
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to start sync.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to start sync.");
    } finally {
      setSyncing(false);
    }
  }

  async function scanStore() {
    if (!storeUrl && !storeHash) {
      toastAlert.displayToast(
        Severity.Warning,
        "Connect your BigCommerce store first."
      );
      return;
    }
    const scanTarget = storeUrl || `${storeHash}.mybigcommerce.com`;
    setScanLoading(true);
    try {
      const resp = await api.admin.post("ScraperJobs/Create", {
        name: `BigCommerce store — ${storeHash}`,
        urls: [`https://${scanTarget}`],
        maxPages: 100
      });
      const data = await resp.json();
      if (data.allowed === false) {
        toastAlert.displayToast(
          Severity.Warning,
          data.reason || "Scan limit reached."
        );
      } else if (data.job_id) {
        setScanDone(true);
        toastAlert.displayToast(
          Severity.Success,
          `Scanning ${scanTarget} — this takes 2–3 minutes.`
        );
      } else {
        toastAlert.displayToast(
          Severity.Error,
          data.error || "Failed to start scan."
        );
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to start scan.");
    } finally {
      setScanLoading(false);
    }
  }

  async function fetchPolicies() {
    const domain =
      storeUrl || (storeHash ? `${storeHash}.mybigcommerce.com` : "");
    if (!domain) {
      setPolicyFetchResult({ error: "Connect your BigCommerce store first." });
      return;
    }
    setFetchingPolicies(true);
    setPolicyFetchResult(null);
    try {
      const resp = await api.admin.get(
        `BigCommerce/FetchPolicies?domain=${encodeURIComponent(domain)}`
      );
      const data = await resp.json();
      if (data.success && Array.isArray(data.pairs)) {
        let filled = 0;
        setPairs((prev) =>
          prev.map((existing) => {
            const match = (data.pairs as QnAPair[]).find(
              (p) => p.id === existing.id
            );
            if (
              match &&
              existing.answer.trim() === "" &&
              match.answer.trim() !== ""
            ) {
              filled++;
              return { ...existing, answer: match.answer };
            }
            return existing;
          })
        );
        setPolicyFetchResult({ count: filled });
      } else {
        setPolicyFetchResult({
          error: data.message || "Could not reach your store policies."
        });
      }
    } catch {
      setPolicyFetchResult({
        error:
          "Could not reach your store policies. Make sure your store is connected and try again."
      });
    } finally {
      setFetchingPolicies(false);
    }
  }

  async function saveToBot() {
    const filledPairs = pairs.filter((p) => p.answer.trim().length > 0);
    if (filledPairs.length === 0) {
      toastAlert.displayToast(
        Severity.Warning,
        "Fill in at least one answer before saving."
      );
      return;
    }
    setSaving(true);
    try {
      const resp = await api.admin.post(
        "BigCommerceBot/PushQnA",
        filledPairs.map((p) => ({ question: p.question, answer: p.answer }))
      );
      const data = await resp.json();
      if (data.success) {
        setSavedCount(data.pushed ?? data.savedCount ?? null);
        setLastSaved(data.lastSaved ?? null);
        toastAlert.displayToast(
          Severity.Success,
          data.message || `${data.pushed} pairs saved to bot.`
        );
      } else {
        toastAlert.displayToast(
          Severity.Error,
          data.message || "Failed to save."
        );
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to save to bot.");
    } finally {
      setSaving(false);
    }
  }

  function addCustomPair() {
    setPairs((prev) => [
      ...prev,
      { id: `custom_${Date.now()}`, question: "", answer: "" }
    ]);
  }

  function removePair(id: string) {
    setPairs((prev) => prev.filter((p) => p.id !== id));
  }

  function updatePair(id: string, field: "question" | "answer", value: string) {
    setPairs((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: value } : p))
    );
  }

  function getNextSyncUTC(): string {
    const now = new Date();
    const next = new Date();
    next.setUTCHours(2, 30, 0, 0);
    if (next.getTime() <= now.getTime()) {
      next.setUTCDate(next.getUTCDate() + 1);
    }
    const diffMs = next.getTime() - now.getTime();
    const diffH = Math.floor(diffMs / 3600000);
    const diffM = Math.floor((diffMs % 3600000) / 60000);
    const label = next.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric"
    });
    const time = next.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short"
    });
    if (diffH < 1) return `in ${diffM}m (${time})`;
    if (diffH < 24) return `in ${diffH}h ${diffM}m (${time})`;
    return `${label} at ${time}`;
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const noAI = !status?.hasAI;
  const noVector = !status?.vectorEnabled;
  const filledCount = pairs.filter((p) => p.answer.trim().length > 0).length;
  const hasStore = Boolean(storeUrl || storeHash);

  return (
    <div className="space-y-4">
      {/* Header */}
      <SectionCard>
        <div className="flex items-start justify-between">
          <div>
            <div className="font-semibold text-zinc-800 text-lg flex items-center gap-2">
              Bot Knowledge
              {savedCount !== null && savedCount > 0 && (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                  {savedCount} pairs saved
                  {lastSaved
                    ? ` · ${new Date(lastSaved).toLocaleDateString()}`
                    : ""}
                </span>
              )}
            </div>
            <p className="text-sm text-zinc-500 mt-1">
              Teach your bot to answer the questions BigCommerce B2B customers
              ask most. Fill in your store's answers and the bot will use them
              in every conversation — instantly, no workflow setup needed.
            </p>
          </div>
        </div>

        {noAI && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
            <strong>AI not enabled on your plan.</strong> Upgrade to enable the
            bot knowledge feature.
          </div>
        )}
        {!noAI && noVector && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
            <strong>Vector index not configured.</strong> Contact support to
            enable bot knowledge indexing.
          </div>
        )}
      </SectionCard>

      {/* Auto-fill from store */}
      <SectionCard>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-zinc-700 flex items-center gap-2">
            <Building className="w-4 h-4" aria-hidden />
            Auto-fill from your store
          </div>
          <p className="text-sm text-zinc-500 mt-0.5">
            Pull your return policy, shipping info, and terms directly from your
            BigCommerce store pages — no copy/paste needed.
          </p>
          {policyFetchResult !== null && "count" in policyFetchResult && (
            <p className="mt-1.5 text-sm text-green-700 font-medium">
              Filled {policyFetchResult.count} of {pairs.length} answers from
              your store policies.
            </p>
          )}
          {policyFetchResult !== null && "error" in policyFetchResult && (
            <p className="mt-1.5 text-sm text-amber-700 font-medium">
              {policyFetchResult.error}
            </p>
          )}
        </div>
        <SecondaryButton
          disabled={fetchingPolicies || !hasStore}
          onClick={fetchPolicies}
          className="shrink-0"
        >
          {fetchingPolicies ? "Fetching policies..." : "Fill from store →"}
        </SecondaryButton>
      </SectionCard>

      {/* Step 1a — Auto-managed Product Index */}
      <SectionCard>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="font-semibold text-zinc-700 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-(--ds-primary) text-white text-xs flex items-center justify-center font-bold shrink-0">
                1
              </span>
              Product catalog — auto-synced daily
            </div>
            <p className="text-sm text-zinc-500 mt-1 ml-8">
              Your product catalog (names, descriptions, SKUs) is indexed for
              semantic discovery — "do you carry hiking gear?", "show me outdoor
              products". Click{" "}
              <strong className="text-zinc-700">Sync Now</strong> to index your
              products for the first time, then daily auto-sync keeps it
              current.{" "}
              <strong className="text-zinc-700">
                Inventory, prices, and orders are always fetched live
              </strong>{" "}
              from BigCommerce during the conversation — never from this index.
            </p>
            {productIndex ? (
              <div className="mt-3 ml-8 flex flex-wrap gap-4 text-xs text-zinc-600">
                <span className="flex items-center gap-1">
                  <span className="font-medium">
                    {productIndex.documentCount.toLocaleString()}
                  </span>{" "}
                  products indexed
                </span>
                <span className="flex items-center gap-1">
                  <span className="font-medium">
                    {productIndex.totalChunks.toLocaleString()}
                  </span>{" "}
                  chunks
                </span>
                {productIndex.lastIndexedAt && (
                  <span className="flex items-center gap-1 text-zinc-400">
                    Last synced:{" "}
                    {new Date(productIndex.lastIndexedAt).toLocaleString()}
                  </span>
                )}
                <span className="flex items-center gap-1 text-zinc-400">
                  Next auto-sync: {getNextSyncUTC()}
                </span>
                {productIndex.hasErrors && (
                  <span className="text-amber-600 font-medium">
                    Some products had errors — check Activity Log
                  </span>
                )}
              </div>
            ) : (
              <div className="mt-3 ml-8 text-xs text-zinc-400">
                {hasStore ? (
                  <>
                    Not yet synced — click{" "}
                    <strong className="text-zinc-600">Sync Now</strong> to index
                    your products for the first time. After that, daily
                    auto-sync handles updates. Next scheduled run:{" "}
                    {getNextSyncUTC()}.
                  </>
                ) : (
                  "Connect your BigCommerce store above to enable product indexing."
                )}
              </div>
            )}
          </div>
          <PrimaryButton
            disabled={syncing || !hasStore}
            onClick={syncProducts}
            className="shrink-0"
          >
            {syncing ? "Starting..." : "Sync Now"}
          </PrimaryButton>
        </div>
        <div className="mt-3 ml-8 p-2 bg-blue-50 border border-blue-100 rounded text-xs text-blue-700">
          <strong>AI Skills answer product questions live.</strong> Stock,
          prices, variants, and orders are fetched from BigCommerce in real time
          during the conversation — never from the index. The index above is for
          semantic catalog browsing ("show me outdoor products").
        </div>
      </SectionCard>

      {/* Step 1b — Scraper for store PAGES (not products) */}
      <SectionCard>
        <div className="flex items-start justify-between">
          <div>
            <div className="font-semibold text-zinc-700 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-zinc-400 text-white text-xs flex items-center justify-center font-bold shrink-0">
                1b
              </span>
              Store pages — policies, FAQs, blog posts (optional)
            </div>
            <p className="text-sm text-zinc-500 mt-1 ml-8">
              <strong className="text-zinc-700">
                For non-product pages only.
              </strong>{" "}
              Your return policy, shipping info, size guides, FAQs, and blog
              posts — content that doesn't change daily and isn't covered by AI
              skills. Product pages are already handled by the sync above, so
              skip them here. Takes 2–3 minutes.
              {hasStore && (
                <>
                  {" "}
                  Target: <strong>{storeUrl || storeHash}</strong>.
                </>
              )}{" "}
              <strong className="text-zinc-700">Tip:</strong> After scanning, go
              to <strong>Chatbot Training → Web</strong> to review pages and
              remove any product URLs before publishing to your index.
            </p>
          </div>
          <SecondaryButton
            disabled={scanLoading || scanDone || !hasStore}
            onClick={scanStore}
            className={`ml-4 shrink-0 ${scanDone ? "!bg-green-100 !text-green-700 cursor-default" : ""}`}
          >
            {scanDone
              ? "Scan started"
              : scanLoading
                ? "Starting..."
                : "Scan pages"}
          </SecondaryButton>
        </div>
        {scanDone && (
          <div className="mt-3 ml-8 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
            Scan running in the background. Go to{" "}
            <strong>Chatbot Training → Web</strong> to see progress and publish
            the pages to your index.
          </div>
        )}
        {!hasStore && (
          <div className="mt-3 ml-8 text-xs text-zinc-400">
            Connect your BigCommerce store above to enable page scanning.
          </div>
        )}
      </SectionCard>

      {/* Step 2 — QnA Builder */}
      <SectionCard>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-semibold text-zinc-700 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-(--ds-primary) text-white text-xs flex items-center justify-center font-bold shrink-0">
                2
              </span>
              Quick Q&amp;A — your store's most common questions
            </div>
            <p className="text-sm text-zinc-500 mt-1 ml-8">
              Fill in your answers below. The bot will use these to answer
              customers instantly. Leave blank any questions that don't apply to
              your store.
            </p>
          </div>
          <div className="flex items-center gap-2 ml-4 shrink-0">
            <span className="text-xs text-zinc-400">
              {filledCount}/{pairs.length} answered
            </span>
            <PrimaryButton
              disabled={saving || filledCount === 0}
              onClick={saveToBot}
              className="!bg-green-600 hover:!opacity-90"
            >
              {saving ? "Saving..." : "Save to bot"}
            </PrimaryButton>
          </div>
        </div>

        <div className="space-y-3 ml-8">
          {pairs.map((pair) => (
            <div
              key={pair.id}
              className="rounded-xl border border-zinc-200 p-4"
            >
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  {pair.id.startsWith("custom_") ? (
                    <input
                      type="text"
                      value={pair.question}
                      onChange={(e) =>
                        updatePair(pair.id, "question", e.target.value)
                      }
                      placeholder="Type your question..."
                      className="w-full text-sm font-medium text-zinc-700 bg-transparent border-b border-zinc-200 pb-1 mb-2 focus:outline-none focus:border-blue-400"
                    />
                  ) : (
                    <div className="text-sm font-medium text-zinc-700 mb-2">
                      {pair.question}
                    </div>
                  )}
                  <textarea
                    rows={3}
                    value={pair.answer}
                    onChange={(e) =>
                      updatePair(pair.id, "answer", e.target.value)
                    }
                    placeholder="Type your answer here — be specific about your store's policy..."
                    className="w-full text-sm text-zinc-600 bg-zinc-50 border border-zinc-200 rounded p-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-300"
                  />
                </div>
                {pair.id.startsWith("custom_") && (
                  <IconButton
                    onClick={() => removePair(pair.id)}
                    aria-label="Remove pair"
                    variant="ghost"
                    size="sm"
                    className="!text-zinc-300 hover:!text-red-400 mt-1 shrink-0"
                  >
                    <svg
                      viewBox="0 0 16 16"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      aria-hidden="true"
                    >
                      <path d="M4 4l8 8M12 4l-8 8" />
                    </svg>
                  </IconButton>
                )}
              </div>
            </div>
          ))}

          <GhostButton onClick={addCustomPair} className="py-1">
            <svg
              viewBox="0 0 16 16"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M8 3v10M3 8h10" />
            </svg>
            Add custom question
          </GhostButton>
        </div>

        <div className="mt-4 ml-8 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
          <strong>Tip:</strong> BigCommerce customers often ask about wholesale
          pricing, lead times, and B2B accounts. Be specific about your policies
          — B2B buyers expect precise answers. Instead of "Contact us", say "Our
          minimum order is 10 units. Volume pricing tiers start at 50 units."
        </div>
      </SectionCard>

      {/* Step 3 — Power Features */}
      <SectionCard>
        <div className="font-semibold text-zinc-700 flex items-center gap-2 mb-1">
          <span className="w-6 h-6 rounded-full bg-(--ds-primary) text-white text-xs flex items-center justify-center font-bold shrink-0">
            3
          </span>
          Power features — beyond basic Q&amp;A
        </div>
        <p className="text-sm text-zinc-500 ml-8 mb-4">
          Your bot can go far beyond answering policy questions. These AI skills
          let it search your product catalog, build custom carts, recover
          abandoned orders, apply discounts, and more — all in the chat window
          without any agent involvement.
        </p>
        <div className="ml-8 grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {[
            {
              label: "Product Search",
              tool: "bigcommerce_search_products",
              desc: "Visitor asks about products — bot searches your catalog, shows pricing, availability, and SKUs.",
              tier: "Professional",
              icon: (
                <svg
                  viewBox="0 0 20 20"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden="true"
                >
                  <circle cx="9" cy="9" r="6" />
                  <path d="M15 15l3 3" strokeLinecap="round" />
                </svg>
              )
            },
            {
              label: "Cart Builder",
              tool: "bigcommerce_create_cart",
              desc: "Visitor selects products — bot builds a BigCommerce cart and sends a direct checkout link. Zero friction.",
              tier: "Professional",
              icon: (
                <svg
                  viewBox="0 0 20 20"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden="true"
                >
                  <path
                    d="M3 3h2l2.5 10h8l2-6H7.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="9" cy="16" r="1" />
                  <circle cx="15" cy="16" r="1" />
                </svg>
              )
            },
            {
              label: "Abandoned Cart Recovery",
              tool: "bigcommerce_get_abandoned_carts",
              desc: "Customer returns after abandoning — bot finds their cart, offers a discount to close the sale.",
              tier: "Professional",
              icon: (
                <svg
                  viewBox="0 0 20 20"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden="true"
                >
                  <path
                    d="M3 10a7 7 0 1 0 14 0H3M10 3l-4 4 4 4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )
            },
            {
              label: "Apply Coupon",
              tool: "bigcommerce_apply_coupon",
              desc: "Bot applies a discount code to the visitor's cart during the conversation to close hesitant buyers.",
              tier: "Professional",
              icon: (
                <svg
                  viewBox="0 0 20 20"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden="true"
                >
                  <rect x="2" y="7" width="16" height="6" rx="2" />
                  <path
                    d="M8 7V5M8 15v-2M12 7V5M12 15v-2"
                    strokeLinecap="round"
                  />
                </svg>
              )
            },
            {
              label: "Order Tracking",
              tool: "bigcommerce_get_order",
              desc: "Customer asks where their order is — bot retrieves live order status, tracking, and carrier info.",
              tier: "Professional",
              icon: (
                <svg
                  viewBox="0 0 20 20"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden="true"
                >
                  <path
                    d="M3 10h14M10 3l7 7-7 7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )
            },
            {
              label: "Customer Lookup",
              tool: "bigcommerce_get_customer",
              desc: "Bot finds the customer's BigCommerce profile — order count, group membership, store credit balance.",
              tier: "Professional",
              icon: (
                <svg
                  viewBox="0 0 20 20"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden="true"
                >
                  <circle cx="10" cy="7" r="4" />
                  <path d="M2 18c0-4 3.6-7 8-7s8 3 8 7" strokeLinecap="round" />
                </svg>
              )
            },
            {
              label: "Live Stock Check",
              tool: "bigcommerce_check_stock",
              desc: "Bot checks real-time inventory by product name — answers 'is this in stock?' with urgency messaging when levels are low.",
              tier: "Professional",
              icon: (
                <svg
                  viewBox="0 0 20 20"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden="true"
                >
                  <rect x="3" y="3" width="14" height="14" rx="2" />
                  <path
                    d="M7 10l2 2 4-4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )
            },
            {
              label: "Shipment Tracking",
              tool: "bigcommerce_get_tracking",
              desc: "Customer asks 'where is my package?' — bot retrieves carrier, tracking number, and a direct tracking link.",
              tier: "Professional",
              icon: (
                <svg
                  viewBox="0 0 20 20"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden="true"
                >
                  <path
                    d="M3 10h14M13 5l5 5-5 5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )
            },
            {
              label: "Product Reviews",
              tool: "bigcommerce_get_reviews",
              desc: "Bot fetches native BigCommerce star ratings and review highlights — answers 'is this product good?' with real social proof.",
              tier: "Professional",
              icon: (
                <svg
                  viewBox="0 0 20 20"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden="true"
                >
                  <path
                    d="M10 2l2.4 5h5.2l-4.2 3.1 1.6 5L10 12l-5 3.1 1.6-5L2.4 7h5.2z"
                    strokeLinejoin="round"
                  />
                </svg>
              )
            },
            {
              label: "Product Variants",
              tool: "bigcommerce_get_variants",
              desc: "Returns all color/size/style variants for a product with per-variant price and stock. Answers 'do you have it in blue, size L?' instantly.",
              tier: "Professional",
              icon: (
                <svg
                  viewBox="0 0 20 20"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden="true"
                >
                  <circle cx="6" cy="10" r="2.5" />
                  <circle cx="14" cy="10" r="2.5" />
                  <path d="M8.5 10h3" strokeLinecap="round" />
                </svg>
              )
            },
            {
              label: "Customer Group Pricing",
              tool: "bigcommerce_check_pricing",
              desc: "Looks up B2B customer-group-specific pricing for a product. Returns the price a specific customer would pay based on their account tier.",
              tier: "Professional",
              icon: (
                <svg
                  viewBox="0 0 20 20"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden="true"
                >
                  <rect x="3" y="5" width="14" height="11" rx="2" />
                  <path d="M7 9h6M7 13h4" strokeLinecap="round" />
                </svg>
              )
            },
            {
              label: "Browse by Brand",
              tool: "bigcommerce_get_brand_products",
              desc: "Returns products filtered by brand name — powers 'show me all Acme products' or 'what brands do you carry?' discovery queries.",
              tier: "Professional",
              icon: (
                <svg
                  viewBox="0 0 20 20"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden="true"
                >
                  <path d="M4 4h12v12H4z" />
                  <path d="M8 4v12M4 8h12" strokeLinecap="round" />
                </svg>
              )
            }
          ].map((f, i) => (
            <div
              key={i}
              className="rounded-xl border border-zinc-200 p-4 flex gap-3"
            >
              <div className="text-blue-500 mt-0.5 shrink-0">{f.icon}</div>
              <div>
                <div className="text-sm font-semibold text-zinc-700 flex items-center gap-2">
                  {f.label}
                  <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                    {f.tier}
                  </span>
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">{f.desc}</div>
                <div className="text-xs text-zinc-400 mt-1 font-mono">
                  {f.tool}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="ml-8 p-3 bg-zinc-50 border border-zinc-200 rounded text-xs text-zinc-600">
          <strong>To enable:</strong> Go to{" "}
          <strong>Bots &rarr; AI Training &rarr; Skills</strong> and enable the
          BigCommerce Commerce skills. These require a Professional plan and a
          connected BigCommerce store. Then use the{" "}
          <strong>Launch Starter Wizard</strong> (Setup tab) to add a
          BigCommerce workflow. 16 tools available: product search, stock check,
          product variants, brand browsing, cart builder, abandoned cart
          recovery, coupon application, customer group pricing, order tracking,
          shipment tracking, product reviews, customer lookup, active cart, add
          order note, and more.
        </div>
      </SectionCard>

      {/* Next steps */}
      <SectionCard>
        <div className="font-semibold text-zinc-700 flex items-center gap-2 mb-3">
          <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs flex items-center justify-center font-bold shrink-0">
            ✓
          </span>
          What to do next
        </div>
        <ol className="ml-8 space-y-2 text-sm text-zinc-600 list-none">
          <li className="flex items-start gap-3">
            <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-bold shrink-0 mt-0.5">
              1
            </span>
            <span>
              <strong>Go to Setup tab</strong> → click{" "}
              <strong>Launch Starter Wizard</strong> to create a BigCommerce AI
              workflow in 60 seconds.
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-bold shrink-0 mt-0.5">
              2
            </span>
            <span>
              <strong>Go to Bots → AI Training → Skills</strong> to enable
              BigCommerce skills (product search, orders, cart, and more).
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-bold shrink-0 mt-0.5">
              3
            </span>
            <span>
              <strong>Go to Deployments</strong> to grab your embed code and add
              the chat widget to your BigCommerce store.
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-bold shrink-0 mt-0.5">
              4
            </span>
            <span>
              <strong>Test your bot</strong> — go to Bots → select your bot →
              Test tab to try it before going live.
            </span>
          </li>
        </ol>
      </SectionCard>
    </div>
  );
}
